"use client";

/**
 * QualidotLogo — renderiza /logo_qualidot_trim.png (blanco) recoloreado vía
 * máscara CSS: el color lo pone la clase de fondo del elemento.
 *
 * Default `bg-foreground`: negro en modo claro, blanco en modo oscuro.
 * Para azul: <QualidotLogo className="bg-primary" />
 *
 * `burst`: FUEGO en canvas alrededor del logo — flamas que nacen en su base y
 * suben lamiéndolo, con turbulencia y brasas aleatorias por frame. Arde ~3.5s
 * al montar y se re-enciende mientras el mouse esté encima. Nada determinístico.
 */

import { useEffect, useRef, type CSSProperties } from "react";

const MASK_STYLE: CSSProperties = {
  WebkitMaskImage: "url(/logo_qualidot_trim.png)",
  maskImage: "url(/logo_qualidot_trim.png)",
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskSize: "contain",
  maskSize: "contain",
  WebkitMaskPosition: "center",
  maskPosition: "center",
};

// Paleta de fuego: núcleo cálido → naranjas → rojos
const FIRE = ["#ffdd55", "#ffb300", "#ff8a00", "#ff6a00", "#ff3800"];
const CORE = "#fff3c4";
const rand = (min: number, max: number) => min + Math.random() * (max - min);
const pick = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];

interface Flame {
  x: number; y: number;
  vx: number; vy: number;
  life: number; maxLife: number;
  size: number;
  color: string;
  /** brasa: puntito brillante que vive más y viaja más alto */
  ember: boolean;
}

/** El canvas mide SCALE× el logo; el glifo queda centrado. */
const SCALE = 4;

export function QualidotLogo({
  size = 28,
  className = "bg-foreground",
  burst = false,
}: {
  /** px (número) o cualquier CSS length válido, ej. "clamp(6rem,18vw,12rem)". El burst de fuego requiere número. */
  size?: number | string;
  className?: string;
  burst?: boolean;
}) {
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const flames     = useRef<Flame[]>([]);
  const emitting   = useRef(false);
  const hovering   = useRef(false);
  const running    = useRef(false);
  const rafId      = useRef(0);
  const prevT      = useRef(0);

  const numericSize = typeof size === "number" ? size : 0; // el burst (canvas) solo aplica con size numérico
  const cssSize = numericSize * SCALE;   // lado del canvas en CSS px
  const C = cssSize / 2;                 // centro (el logo vive ahí)
  const half = numericSize / 2;

  const spawnFlames = (dt: number) => {
    // Tasa proporcional al tiempo: ~5 flamas por frame de 16ms
    const n = Math.max(1, Math.round((dt / 16) * rand(3, 6)));
    for (let i = 0; i < n; i++) {
      const ember = Math.random() < 0.18;
      flames.current.push({
        // Nacen en la base del glifo (mitad inferior, ancho completo)
        x: C + rand(-half * 1.05, half * 1.05),
        y: C + rand(half * 0.05, half * 0.75),
        vx: rand(-0.35, 0.35),
        vy: -rand(0.8, 2.6) * (ember ? 1.5 : 1),   // el fuego SUBE
        life: 0,
        maxLife: ember ? rand(700, 1300) : rand(320, 750),
        size: ember ? rand(1.5, 3) : rand(3, half * 0.45),
        color: pick(FIRE),
        ember,
      });
    }
  };

  const frame = (now: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) { running.current = false; return; }

    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== cssSize * dpr) {
      canvas.width = cssSize * dpr;
      canvas.height = cssSize * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    const dt = Math.min(50, now - (prevT.current || now));
    prevT.current = now;

    if (emitting.current) spawnFlames(dt);

    ctx.clearRect(0, 0, cssSize, cssSize);
    ctx.globalCompositeOperation = "lighter";

    for (let i = flames.current.length - 1; i >= 0; i--) {
      const f = flames.current[i];
      f.life += dt;
      const lp = f.life / f.maxLife;
      if (lp >= 1) { flames.current.splice(i, 1); continue; }

      // Turbulencia lateral (el lamido del fuego) + aceleración ascendente
      f.vx += rand(-0.3, 0.3);
      f.vx *= 0.92;
      f.vy -= 0.02 * (dt / 16);
      f.x += f.vx * (dt / 16);
      f.y += f.vy * (dt / 16);

      // La flama encoge y se apaga al subir; parpadeo aleatorio por frame
      const a = (lp < 0.15 ? lp / 0.15 : 1 - (lp - 0.15) / 0.85) * rand(0.7, 1);
      const r = f.size * (1 - lp * 0.6);

      if (f.ember) {
        ctx.globalAlpha = a;
        ctx.fillStyle = CORE;
        ctx.beginPath();
        ctx.arc(f.x, f.y, r, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // Cuerpo de flama: núcleo cálido → color → transparente
        const g = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, r * 2.2);
        g.addColorStop(0, CORE);
        g.addColorStop(0.35, f.color);
        g.addColorStop(1, "rgba(0,0,0,0)");
        ctx.globalAlpha = a * 0.75;
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(f.x, f.y, r * 2.2, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    if (flames.current.length === 0 && !emitting.current) {
      ctx.clearRect(0, 0, cssSize, cssSize);
      running.current = false;
      return;
    }
    rafId.current = requestAnimationFrame(frame);
  };

  const ensureLoop = () => {
    if (running.current) return;
    running.current = true;
    prevT.current = 0;
    rafId.current = requestAnimationFrame(frame);
  };

  // Ignición al montar (~3.5s) — sigue ardiendo si el mouse está encima
  useEffect(() => {
    if (!burst) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    emitting.current = true;
    ensureLoop();
    const t = setTimeout(() => { emitting.current = hovering.current; }, 3500);
    return () => { clearTimeout(t); cancelAnimationFrame(rafId.current); running.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [burst]);

  const logo = (
    <span
      role="img"
      aria-label="Qualidot"
      className={`inline-block shrink-0 relative z-[1] ${className}`}
      style={{ width: size, height: size, ...MASK_STYLE }}
    />
  );

  if (!burst) return logo;

  return (
    <span
      className="relative inline-block shrink-0"
      style={{ width: size, height: size }}
      onMouseEnter={() => { hovering.current = true; emitting.current = true; ensureLoop(); }}
      onMouseLeave={() => { hovering.current = false; emitting.current = false; }}
    >
      {/* Canvas del fuego: 4× el logo, sin máscara ni sombras — flamas libres */}
      <canvas
        ref={canvasRef}
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          width: cssSize,
          height: cssSize,
          left: -(cssSize - numericSize) / 2,
          top: -(cssSize - numericSize) / 2,
        }}
      />
      {logo}
    </span>
  );
}
